<template>
  <div>
    <div v-if="! isModal" class="level is-variable bd-klmn-columns is-1 is-marginless">
      <div class="level-left">
        <b-tooltip :label="$t('clearFilter')" type="is-dark" position="is-right">
          <b-button
            class="filter-button is-hidden-mobile"
            size="is-circle"
            icon-left="filter-remove"
            @click="clearFilter"
          />
        </b-tooltip>

        <div class="filter-tags">
          <b-tag
            v-for="(filter, index) in filters"
            :key="index"
            type="is-purple"
            closable
            @close="removeFilter(filter)"
          >
            {{ $t('filter.' + filter) }}
          </b-tag>
        </div>
      </div>

      <div class="level-right">
        <slot name="header-right" />
      </div>
    </div>

    <b-table
      :data="data"
      :loading="isLoading"

      :current-page.sync="page"
      :total="total"
      :per-page="perPage"

      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      striped
      sticky-header
      paginated
      hoverable
      backend-sorting
      backend-pagination

      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"

      class="no-wrap custom-table"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <template slot-scope="props">
        <b-table-column custom-key="actions" class="is-actions-cell" sticky>
          <b-tooltip v-if="checkPermission('delete_transfer')" :label="$t('delete')" type="is-dark" position="is-right">
            <b-button
              outlined
              size="is-small"
              class="button-green is-danger mr-5 only-icon"
              icon-left="close-circle"
              @click="deleteTransfer(props.row.id)"
            />
          </b-tooltip>

          <b-tooltip v-if="checkPermission('create_transfer')" :label="$t('copy')" type="is-dark" position="is-right">
            <b-button
              outlined
              size="is-small"
              class="button-green is-info mr-5"
              icon-left="content-copy"
              tag="router-link"
              :to="{ name: 'transfers.create', query: { id: props.row.id }}"
            />
          </b-tooltip>

          <b-button
            outlined
            size="is-small"
            class="button-purble btn-details"
            icon-left="arrow-right-bold"
            tag="a"
            :href="transferURL(props.row.id)"
            :target="transferTarget"
            @click.prevent="showTransfer(props.row.id)"
          >
            {{ $t('details') }}
          </b-button>

          <b-tooltip label="Yazdır" type="is-dark" position="is-right">
            <b-button
              outlined
              size="is-small"
              class="button-purble btn-details ml-5"
              icon-left="printer"
              tag="a"
              @click.prevent="onPrint(props.row.id)"
            />
          </b-tooltip>
        </b-table-column>

        <b-table-column :label="$t('filter.id')" field="id" number sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="id-search search-dropdown" :close-on-click="false" trap-focus>
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item" :focusable="false">
                <b-input v-model="queryData.id" :placeholder="$t('idSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>

            {{ column.label }}
          </template>
          #{{ props.row.id }}
        </b-table-column>

        <b-table-column :label="$t('filter.plate')" field="car_plate" centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.plate" :placeholder="$t('plateSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <small v-if="props.row.car_plate" class="tag is-abbr-like">
            {{ props.row.car_plate }}
          </small>
        </b-table-column>

        <b-table-column :label="$t('filter.status')" field="status" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown v-model="queryData.status" multiple aria-role="list" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item
                v-for="(status, index) in transferStatus"
                :key="index"
                :value="index"
                aria-role="listitem"
              >
                <span>{{ status }}</span>
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <span
            class="tag"
            :class="[{ 'has-suggestion-mark': props.row.suggestion}, $options.filters.showStatus(props.row.status)]"
          >
            {{ $t(`status.${props.row.status}`) }}
          </span>
        </b-table-column>

        <b-table-column :label="$t('filter.driver')" centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item" :focusable="false">
                <b-input v-model="queryData.driver" :placeholder="$t('driverSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <span v-if="props.row.driver">
            {{ props.row.driver.name | limitShow }}
          </span>
        </b-table-column>

        <b-table-column :label="$t('filter.secondDriver')" centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item" :focusable="false">
                <b-input v-model="queryData.second_driver" :placeholder="$t('driverSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <span v-if="props.row.second_driver">
            {{ props.row.second_driver.name | limitShow }}
          </span>
        </b-table-column>

        <b-table-column label="Görev Tarihi" field="transfer_date" sortable date centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="date-search search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item custom paddingless aria-role="menu-item" :focusable="false">
                <b-datepicker
                  v-model="queryData.dateRange"
                  :placeholder="$t('dateSearch')"
                  :first-day-of-week="1"
                  :month-names="$t('dateTimePicker.monthNames')"
                  :day-names="$t('dateTimePicker.dayNames')"
                  icon="calendar-today"
                  range
                  inline
                >
                  <div class="button is-danger" @click="queryData.dateRange = []">
                    {{ $t('clear') }}
                  </div>
                </b-datepicker>
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <small class="tag is-abbr-like" :title="props.row.transfer_date">
            {{ props.row.transfer_date | formatDate }}
          </small>
        </b-table-column>

        <b-table-column label="Talep Eden" centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item" :focusable="false">
                <b-input v-model="queryData.demandant_name" :placeholder="$t('nameSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <b-tooltip
            v-if="props.row.demandant_name"
            :label="props.row.demandant_name"
            :active="props.row.demandant_name.length > 20"
            type="is-dark"
            position="is-bottom"
          >
            {{ props.row.demandant_name | limitShow }}
          </b-tooltip>
        </b-table-column>

        <b-table-column label="Talep Eden Birim" field="request_unit" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.request_unit" :placeholder="$t('search')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <template v-if="props.row.request_unit_specification">
            <b-tooltip :label="props.row.request_unit_specification.name" type="is-dark" position="is-bottom">
              {{ props.row.request_unit_specification.name | limitShow }}
            </b-tooltip>
          </template>
        </b-table-column>

        <b-table-column v-if="isFieldVisible('transfer', 'requested_unit_id')" label="Talep Edilen Birim" field="requested_unit" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.requested_unit" :placeholder="$t('search')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <template v-if="props.row.requested_unit_specification">
            <b-tooltip :label="props.row.requested_unit_specification.name" type="is-dark" position="is-bottom">
              {{ props.row.requested_unit_specification.name | limitShow }}
            </b-tooltip>
          </template>
        </b-table-column>

        <b-table-column label="Varış İl" field="to_province" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.to_province" :placeholder="$t('targetSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          {{ props.row.to_province.name }}
        </b-table-column>

        <b-table-column v-if="isFieldVisible('transfer', 'to_district_id')" label="Varış İlçe" field="to_district" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.to_district" :placeholder="$t('targetSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          {{ props.row.to_district ? props.row.to_district.name : '' }}
        </b-table-column>

        <b-table-column v-if="isFieldVisible('transfer', 'to_neighborhood_id')" label="Varış Mahalle" field="to_neighborhood" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.to_neighborhood" :placeholder="$t('targetSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          {{ props.row.to_neighborhood ? props.row.to_neighborhood.name : '' }}
        </b-table-column>

        <b-table-column label="Varış Açık Adres" field="to_address_line" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.to_address_line" :placeholder="$t('targetSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <b-tooltip :label="props.row.to_address_line" type="is-dark" position="is-bottom">
            {{ props.row.to_address_line | limitShow }}
          </b-tooltip>
        </b-table-column>

        <b-table-column label="Kalkış İl" field="from_province" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.from_province" :placeholder="$t('departureSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          {{ props.row.from_province.name }}
        </b-table-column>

        <b-table-column v-if="isFieldVisible('transfer', 'from_district_id')" label="Kalkış İlçe" field="from_district" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.from_district" :placeholder="$t('departureSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          {{ props.row.from_district ? props.row.from_district.name : '' }}
        </b-table-column>

        <b-table-column v-if="isFieldVisible('transfer', 'from_neighborhood_id')" label="Kalkış Mahalle" field="from_neighborhood" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.from_neighborhood" :placeholder="$t('departureSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          {{ props.row.from_neighborhood ? props.row.from_neighborhood.name : '' }}
        </b-table-column>

        <b-table-column label="Kalkış Açık Adres" field="from_address_line" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.from_address_line" :placeholder="$t('departureSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <b-tooltip :label="props.row.from_address_line" type="is-dark" position="is-bottom">
            {{ props.row.from_address_line | limitShow }}
          </b-tooltip>
        </b-table-column>

        <b-table-column label="Başlagıç Tarihi/Saati" field="start_time_range" sortable date centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="date-search search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item custom paddingless aria-role="menu-item" :focusable="false">
                <b-datepicker
                  v-model="queryData.startTimeRange"
                  :placeholder="$t('dateSearch')"
                  :first-day-of-week="1"
                  :month-names="$t('dateTimePicker.monthNames')"
                  :day-names="$t('dateTimePicker.dayNames')"
                  :years-range="[-((new Date).getFullYear() - 2018), 20]"
                  icon="calendar-today"
                  range
                  inline
                >
                  <div class="button is-danger" @click="queryData.startTimeRange = []">
                    {{ $t('clear') }}
                  </div>
                </b-datepicker>
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <small v-if="props.row.start_time" class="tag is-abbr-like" :title="props.row.start_time">
            {{ props.row.start_time | formatDate }}
          </small>
        </b-table-column>

        <b-table-column label="Bitiş Tarihi/Saati" field="end_time_range" sortable date centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="date-search search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item custom paddingless aria-role="menu-item" :focusable="false">
                <b-datepicker
                  v-model="queryData.endTimeRange"
                  :placeholder="$t('dateSearch')"
                  :first-day-of-week="1"
                  :month-names="$t('dateTimePicker.monthNames')"
                  :day-names="$t('dateTimePicker.dayNames')"
                  :years-range="[-((new Date).getFullYear() - 2018), 20]"
                  icon="calendar-today"
                  range
                  inline
                >
                  <div class="button is-danger" @click="queryData.endTimeRange = []">
                    {{ $t('clear') }}
                  </div>
                </b-datepicker>
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <small v-if="props.row.end_time" class="tag is-abbr-like" :title="props.row.end_time">
            {{ props.row.end_time | formatDate }}
          </small>
        </b-table-column>

        <b-table-column :label="$t('type')" field="self_drive" centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown v-model="queryData.self_drive" aria-role="list" :close-on-click="true">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item value="0" aria-role="listitem">
                <span>{{ $t('selfDrive.false') }}</span>
              </b-dropdown-item>
              <b-dropdown-item value="1" aria-role="listitem">
                <span>{{ $t('selfDrive.true') }}</span>
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <small :class="`tag is-abbr-like self-column-${props.row.self_drive}`">
            {{ $t(`selfDrive.${props.row.self_drive}`) }}
          </small>
        </b-table-column>

        <b-table-column label="Marka" field="brand" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.brand" placeholder="Marka Ara" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <template v-if="props.row.car">
            {{ props.row.car.brand }}
          </template>
        </b-table-column>

        <b-table-column label="Model" field="model" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.model" placeholder="Model Arama" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <template v-if="props.row.car">
            {{ props.row.car.model }}
          </template>
        </b-table-column>

        <b-table-column label="Cinsi" field="vehicle_type" centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.vehicle_type" placeholder="Cins Arama" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <template v-if="props.row.car && props.row.car.vehicle_type">
            {{ props.row.car.vehicle_type.name }}
          </template>
        </b-table-column>

        <b-table-column label="Koltuk Sayısı" field="number_of_seats" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="list" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item">
                <b-input v-model="queryData.number_of_seats" placeholder="Koltuk Sayısı Arama" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <template v-if="props.row.car">
            {{ props.row.car.number_of_seats }}
          </template>
        </b-table-column>

        <b-table-column :label="$t('transferDuration')" field="road_time" sortable centered>
          <small v-if="props.row.road_time" class="tag is-abbr-like">
            {{ props.row.road_time }}
          </small>
        </b-table-column>

        <b-table-column :label="$t('transferDistance')" field="road_distance" sortable centered>
          <small v-if="props.row.road_distance" class="tag is-abbr-like">
            {{ props.row.road_distance }}
          </small>
        </b-table-column>

        <b-table-column label="KM" centered>
          <small v-if="isNumber(props.row.end_km) && isNumber(props.row.start_km)" class="tag is-abbr-like">
            {{ km(props.row.end_km, props.row.start_km) }}
          </small>
        </b-table-column>

        <b-table-column label="Oluşturulma Tarihi" field="created_at" sortable date centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="date-search search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item custom paddingless aria-role="menu-item" :focusable="false">
                <b-datepicker
                  v-model="queryData.createdAtRange"
                  :placeholder="$t('dateSearch')"
                  :first-day-of-week="1"
                  :month-names="$t('dateTimePicker.monthNames')"
                  :day-names="$t('dateTimePicker.dayNames')"
                  icon="calendar-today"
                  range
                  inline
                >
                  <div class="button is-danger" @click="queryData.createdAtRange = []">
                    {{ $t('clear') }}
                  </div>
                </b-datepicker>
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <small class="tag is-abbr-like" :title="props.row.created_at">
            {{ props.row.created_at | formatDate }}
          </small>
        </b-table-column>

        <b-table-column label="Oluşturan Kullanıcı" centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item" :focusable="false">
                <b-input v-model="queryData.name" :placeholder="$t('nameSearch')" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>

          <b-tooltip
            v-if="props.row.user"
            :label="props.row.user.name"
            :active="props.row.user.name.length > 20"
            type="is-dark"
            position="is-bottom"
          >
            {{ props.row.user.name | limitShow }}
          </b-tooltip>
        </b-table-column>

        <b-table-column label="Oluşturan Kullanıcı Açıklaması" field="user_description" sortable centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item" :focusable="false">
                <b-input v-model="queryData.user_description" :placeholder="'Açıklama Arama'" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <span v-if="props.row.user_description">
            {{ props.row.user_description | limitShow }}
          </span>
        </b-table-column>

        <b-table-column label="Görev Türü" field="duty_type_specification" centered>
          <template slot="header" slot-scope="{ column }">
            <b-dropdown aria-role="menu" class="search-dropdown" :close-on-click="false">
              <b-icon slot="trigger" icon="magnify" class="search-icon pointer" custom-size="mdi-20px" />
              <b-dropdown-item aria-role="menu-item" :focusable="false">
                <b-input v-model="queryData.duty_type_specification" :placeholder="'Görev Türü Arama'" size="is-small" autocomplete="off" type="search" />
              </b-dropdown-item>
            </b-dropdown>
            {{ column.label }}
          </template>
          <span v-if="props.row.duty_type_specification">
            {{ props.row.duty_type_specification.name | limitShow }}
          </span>
        </b-table-column>
      </template>

      <template slot="bottom-left">
        <div v-if="total > 0" class="has-text-left subtitle help">
          {{ $t('paginationShowing', [total.toLocaleString(), ((page * perPage) - perPage) + 1, ((page * perPage) > total ? total : (page * perPage)) ]) }}
        </div>
      </template>

      <section slot="empty" class="section">
        <div class="content has-text-grey has-text-centered">
          <template v-if="!isLoading">
            <p>
              <b-icon icon="table" size="is-large" />
            </p>
            <p>{{ $t('nodata') }}</p>
          </template>
        </div>
      </section>
    </b-table>

    <b-modal
      :active.sync="isTransferModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      animation=""
      aria-modal
    >
      <div class="modal-card full-width" style="width: auto">
        <section class="modal-card-body full-width">
          <transfer-details
            :id="transferId"
            :isModal="true"
            :driverId="driverId"
            :seconDriverId="secondDriverId"
            :carId="carId"
          />
        </section>
      </div>
    </b-modal>
    <iframe :src="printUrl" frameborder="0" style="display:block; visibility:hidden; width:0; height:0;" />
  </div>
</template>

<script>
import {mapGetters, mapState} from 'vuex'
import moment from 'moment'
import debounce from 'lodash/debounce'
import download from 'downloadjs'
import $ from 'jquery'

export default {
  name: 'TransfersTable',
  components: {
    TransferDetails: () => import(/* webpackPrefetch: true */ '@/views/TransferDetails'),
  },
  props: {
    dataUrl: {
      type: String,
      default: null
    },
    checkable: {
      type: Boolean,
      default: false
    },
    isModal: {
      type: Boolean,
      default: false
    },
    where: {
      type: Object,
      default: () => {}
    },
    driverId: {
      type: Number,
      default: null
    },
    secondDriverId: {
      type: Number,
      default: null
    },
    carId: {
      type: Number,
      default: null
    }
  },
  data () {
    const transferStatus = this.$i18n.t('status');

    return {
      transferStatus: transferStatus,
      printUrl: '#',
      isTransferModalActive: false,
      transferId: null,
      trashObject: null,
      data: [],
      isLoading: false,
      perPage: 50,
      sortField: 'id',
      sortOrder: 'desc',
      defaultSortOrder: 'desc',
      total: 0,
      page: 1,
      count: 0,
      inputTrigger: null,
      queryData: {
        id: '',
        user_name: '',
        demandant_name: '',
        from_province: '',
        from_district: '',
        from_neighborhood: '',
        from_address_line: '',
        to_province: '',
        to_district: '',
        to_neighborhood: '',
        to_address_line: '',
        brand: '',
        model: '',
        driver: '',
        second_driver: '',
        plate: '',
        vehicle_type: '',
        name: '',
        startTimeRange: [],
        endTimeRange: [],
        createdAtRange: [],
        dateRange: [],
        status: [],
        self_drive: null,
        request_unit: '',
        excel: 0,
        user_description: '',
        duty_type_specification: '',
      },
      filters: [],
    }
  },
  computed: {
    ...mapState([
      'boardRefresh',
    ]),
    ...mapGetters({
      isFieldVisible: 'settings/isFieldVisible',
    }),
    transferTarget () {
      if (this.isModal) {
        return '_blank'
      }

      return '_self'
    },
  },
  watch: {
    query () {
      this.filterAction()
    },
    queryData: {
      handler () {
        this.filterAction()
      },
      deep: true
    },
    'queryData.dateRange' () {
      setTimeout(function () {
        $('body').trigger('click');
      }, 250);
    },
    'queryData.createdAtRange' () {
      setTimeout(function () {
        $('body').trigger('click');
      }, 250);
    },
    'queryData.startTimeRange' () {
      setTimeout(function () {
        $('body').trigger('click');
      }, 250);
    },
    'queryData.endTimeRange' () {
      setTimeout(function () {
        $('body').trigger('click');
      }, 250);
    },
    'queryData.id' (n) {
      if (n.indexOf('#') > -1) {
        this.queryData.id = n.replace('#', '');
      }
    },
    boardRefresh (n) {
      if (n == true) {
        this.loadTransfers()
      }
    }
  },
  mounted () {
    if (this.where) {
      this.queryData = this.$_.merge(this.queryData, this.where);
    }
    this.isLoading = true
    this.loadTransfers()
  },
  methods: {
    filterAction: debounce(function () {
      this.filterShow()
      this.loadTransfers();
    }, 350),

    clearFilter () {
      this.queryData = {
        id: '',
        user_name: '',
        demandant_name: '',
        from_province: '',
        from_district: '',
        from_neighborhood: '',
        from_address_line: '',
        to_province: '',
        to_district: '',
        to_neighborhood: '',
        to_address_line: '',
        brand: '',
        model: '',
        driver: '',
        second_driver: '',
        plate: '',
        vehicle_type: '',
        self_drive: null,
        startTimeRange: [],
        endTimeRange: [],
        name: '',
        createdAtRange: [],
        dateRange: [],
        status: [],
        request_unit: '',
        excel: 0,
        user_description: '',
        duty_type_specification: '',
      }
      this.filterShow()
      // this.loadTransfers();
    },
    loadTransfers () {
      if (!this.dataUrl) {
        return
      }

      this.$emit('loadingChange', true)

      let responseType = this.queryData.excel ? 'blob' : 'json'

      this.$http({
          url: this.dataUrl,
          method: 'GET',
          responseType: responseType,
          params: {
            ...this.queryData,
            sort_by: this.sortField,
            sort_order: this.sortOrder,
            page: this.page,
            created_at: this.queryData.createdAtRange.length === 2
              ? [moment(this.queryData.createdAtRange[0]).format('YYYY-MM-DD'), moment(this.queryData.createdAtRange[1]).format('YYYY-MM-DD')]
              : null,
            date: this.queryData.dateRange.length === 2
              ? [moment(this.queryData.dateRange[0]).format('YYYY-MM-DD'), moment(this.queryData.dateRange[1]).format('YYYY-MM-DD')]
              : null,
            start_time_range: this.queryData.startTimeRange.length === 2
              ? [moment(this.queryData.startTimeRange[0]).format('YYYY-MM-DD'), moment(this.queryData.startTimeRange[1]).format('YYYY-MM-DD')]
              : null,
            end_time_range: this.queryData.endTimeRange.length === 2
              ? [moment(this.queryData.endTimeRange[0]).format('YYYY-MM-DD'), moment(this.queryData.endTimeRange[1]).format('YYYY-MM-DD')]
              : null,
          }
        })
        .then(({ data }) => {
          if (this.queryData.excel) {
            const mimType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            const fileName = `transfers-${moment().format('DD-MM-YYYY-h-mm')}.xlsx`
            download(data, fileName, mimType)
            this.isLoading = false
            this.$emit('excelChange', false)
            this.$emit('loadingChange', false)
            this.queryData.excel = 0
            return false;
          }

          this.data = data.data ?? []
          this.total = data.meta.total ?? 0
          this.perPage = data.meta.per_page ?? this.perPage
          this.page = data.meta.current_page ?? this.page
          this.count = data.meta.to ?? this.count

          if (data.data.length > 0) {
            window.scrollTo(0, 0)
          }
        })
        .catch(e => {
          this.data = []
          this.$buefy.toast.open({
            message: `Error: ${e.message}`,
            type: 'is-danger'
          })
        })
        .then(() => {
          this.$nextTick(() => {
            this.isLoading = false
            this.$emit('loadingChange', false)
            this.$emit('excelChange', false)

            this.$store.commit('boardRefresh', false)
            this.jqInputTrigger()
          })
        })
    },
    jqInputTrigger () {
      if (this.inputTrigger) return;

      $('.dropdown-trigger').click(function (e) {
        e.stopPropagation()
        $(this).parent().find('input, select').first().focus()
      })
      $('.search-dropdown input').keypress(function (e) {
        if (e.which == '13') {
          $('body').trigger('click')
        }
      });
      $('.dropdown').click(function (e) {
        e.stopPropagation()
      })
      this.inputTrigger = true;
    },
    onPageChange (page) {
      this.page = page
      this.loadTransfers()
    },
    onSort (field, order) {
      this.page = 1
      this.sortField = field
      this.sortOrder = order
      this.loadTransfers()
    },
    type (value) {
      const number = parseFloat(value)
      if (number < 6) {
        return 'is-danger'
      } else if (number >= 6 && number < 8) {
        return 'is-warning'
      } else if (number >= 8) {
        return 'is-success'
      }
    },
    filterShow () {
      this.filters = this.$_.keys(this.$_.omitBy(this.queryData, this.$_.isEmpty))
    },
    removeFilter (filter) {
      this.queryData[filter] = this.$_.isArray(this.queryData[filter]) ? [] : '';
      this.filterShow()
    },
    exportExcel () {
      this.queryData.excel = 1;
    },
    deleteTransfer (id) {
      this.$buefy.dialog.confirm({
        message: 'Emin misiniz?',
        cancelText: this.$t('no'),
        confirmText: this.$t('yes'),
        type: 'is-danger',
        onConfirm: () => {
          this.$http
            .delete('transfers/' + id)
            .then(() => {
              this.$buefy.toast.open({
                message: this.$t('success'),
                type: 'is-warning'
              })
              this.$store.commit('boardRefresh', true)
            })
        }
      })
    },
    transferURL (transerId) {
      let object = {
        name: 'transfers.show',
        params: { id: transerId },
        query: {}
      }
      if (this.carId) {
        object.query.car_id = this.carId
      }

      if (this.driverId) {
        object.query.driver_id = this.driverId
      }

      if (this.seconDriverId) {
        object.query.second_driver_id = this.seconDriverId
      }

      return this.$router.resolve(object).href
    },
    showTransfer (transferId) {
      this.transferId = transferId;
      this.isTransferModalActive = true;
    },
    onPrint (transferId) {
      this.$buefy.toast.open({
        message: 'Lütfen bekleyiniz...',
        type: 'is-info'
      })
      this.$http.get(`transfers/${transferId}/print`, { responseType: 'blob' }).then(({ data }) => {
        this.print(URL.createObjectURL(data));
      })
    },

    print (fileURL) {
      let iframe = this._printIframe;
      if (!this._printIframe) {
        iframe = this._printIframe = document.createElement('iframe');
        document.body.appendChild(iframe);

        iframe.style.display = 'none';
        iframe.onload = () =>{
          setTimeout(() => {
            iframe.focus();
            iframe.contentWindow.print();
          }, 1);
        };
      }

      iframe.src = fileURL;
    },

    isNumber (value) {
      return !isNaN(parseFloat(value)) && isFinite(value)
    },
    km (end, start) {
      return Math.abs(parseInt(end) - parseInt(start)) || null
    },
  }
}
</script>
